import { Node, mergeAttributes } from '@tiptap/core'
import { buildResourceTableInsertContent } from '../lib/post_block_indent.js'
import { Plugin, PluginKey } from 'prosemirror-state'
import { syncResourceRowParagraphsForResource } from '../lib/sync_tiptap_resource_row_titles.js'
import {
  afterResourceRowInsertFocus,
  paragraphRowRole,
  resolveResourceTableRowInsert,
  resourceRowTitleText,
  RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
} from '../lib/resource_row_paragraph.js'

// Debounce utility for resource title updates (mirrors DownloadExtension timing)
const createDebouncedResourceUpdate = () => {
  const timeouts = new Map(); // resourceId -> timeout

  return (resourceId, newTitle) => {
    // Don't send update if resourceId is missing or title is empty/whitespace only
    if (!resourceId) return;
    
    // If title is empty, clear any pending timeout and don't send update
    if (!newTitle || typeof newTitle !== 'string' || newTitle.trim().length === 0) {
      if (timeouts.has(resourceId)) {
        clearTimeout(timeouts.get(resourceId));
        timeouts.delete(resourceId);
      }
      return;
    }

    // Clear existing timeout for this resource
    if (timeouts.has(resourceId)) {
      clearTimeout(timeouts.get(resourceId));
    }

    // Set new timeout with longer delay to prevent updates during rapid deletion
    // If user is deleting quickly, the timeout will be cleared before it fires
    const timeout = setTimeout(async () => {
      timeouts.delete(resourceId);
      
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
      
      try {
        const trimmedTitle = newTitle.trim();
        // Double-check title is not empty before sending
        if (trimmedTitle.length === 0) {
          return;
        }
        
        const response = await fetch(`/admin/resources/${resourceId}.json`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': csrfToken
          },
          body: JSON.stringify({
            resource: {
              title: trimmedTitle
            }
          })
        });

        if (!response.ok) {
          console.warn('Failed to update resource title:', await response.text());
          return;
        }
        syncResourceRowParagraphsForResource('externalLinkRow', resourceId, trimmedTitle);
      } catch (error) {
        console.error('Error updating resource title:', error);
      }
    }, 1500); // Increased from 500ms to 1500ms to prevent updates during rapid deletion

    timeouts.set(resourceId, timeout);
  };
};

const debouncedResourceUpdate = createDebouncedResourceUpdate();

export const ExternalLinkExtension = Node.create({
  name: 'externalLink',

  group: 'block',

  content: 'externalLinkRow+',

  defining: true,

  addAttributes() {
    return {}
  },

  parseHTML() {
    return []
  },

  renderHTML() {
    // This extension no longer renders containers
    return ['div']
  },

  addCommands() {
    return {
      setExternalLink:
        (options) =>
        ({ commands, editor, state }) => {
          const title = options?.title || 'Click to edit title';
          const ok = commands.insertContent(
            buildResourceTableInsertContent(state, [
              {
                type: 'externalLinkRow',
                attrs: options,
                content: [
                  {
                    type: 'paragraph',
                    attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                    content: [{ type: 'text', text: title }],
                  },
                ],
              },
            ])
          );
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'externalLinkRow',
            });
          }
          return ok;
        },

      addExternalLinkRow:
        (options) =>
        ({ commands, editor, state }) => {
          const insertCtx = resolveResourceTableRowInsert(state);
          const ok = commands.insertContent({
            type: 'externalLinkRow',
            attrs: options,
            content: [
              {
                type: 'paragraph',
                attrs: RESOURCE_ROW_TITLE_PARAGRAPH_ATTRS,
                content: [{ type: 'text', text: 'Click to edit title' }],
              }
            ]
          });
          if (ok && editor) {
            afterResourceRowInsertFocus(editor, state, {
              resourceId: options?.resourceId,
              rowType: 'externalLinkRow',
              insertCtx,
            });
          }
          return ok;
        },
    }
  },
})

export const ExternalLinkRowExtension = Node.create({
  name: 'externalLinkRow',

  group: 'block',

  content: 'paragraph+',

  defining: true,

  addAttributes() {
    return {
      href: {
        default: null,
      },
      resourceId: {
        default: null,
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'tr[data-resource-row]',
        priority: 100, // High priority to parse first
        getAttrs: element => {
          // Extract href from various possible attributes
          const href = element.getAttribute('data-href') || 
                      element.getAttribute('href') || 
                      null;
          const resourceId = element.getAttribute('data-resource-id') || null;
          // Check if this is an external link row by looking for link icon or data-row-type
          const isExternalLink =
            element.querySelector('img[src*="link-icon.svg"]') ||
            element.querySelector('.icon-ps-link') ||
            element.getAttribute('data-row-type') === 'externalLink';
          return isExternalLink ? { href, resourceId, rowType: 'externalLink' } : false;
        },
        contentElement: element => {
          // Find the text cell (second td)
          const textCell = element.querySelector('td:last-child');
          return textCell;
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const { href, resourceId } = HTMLAttributes;

    const elementAttrs = mergeAttributes(HTMLAttributes, {
      'data-resource-row': '',
      'data-row-type': 'externalLink',
    });

    if (resourceId) {
      elementAttrs['data-resource-id'] = resourceId.toString();
    }

    if (href) {
      // Add click handler attributes if href is provided
      const formattedUrl = href.startsWith('http://') || href.startsWith('https://') ? href : `https://${href}`;
      
      elementAttrs.style = 'cursor: pointer;';
      elementAttrs.onclick = `
        const editor = window.currentTiptapEditor;
        if (!editor || !editor.isEditable) {
          // If not editable, open URL on single click
          window.open('${formattedUrl}', '_blank');
        }
        // If editable, do nothing on single click
      `;
      elementAttrs['data-href'] = formattedUrl;
    }

    return [
      'tr',
      elementAttrs,
      [
        'td',
        { 
          colspan: '1', 
          rowspan: '1'
        },
        [
          'span',
          {
            class: 'ps-resource-row__icon',
            draggable: 'false',
            contenteditable: 'false',
            'data-resource-row-icon': 'externalLink',
          },
          ['span', { class: 'icon-ps-link', 'aria-hidden': 'true' }],
        ],
      ],
      [
        'td',
        { 
          colspan: '1', 
          rowspan: '1',
          class: 'external-link-title font-primary-bold',
        },
        0 // This will be replaced with the actual content
      ]
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('externalLinkRowKeyupHandler'),
        props: {
          handleDOMEvents: {
            keyup: (view, event) => {
              const { state } = view;
              const { selection } = state;
              const { $from } = selection;

              // Find if we're inside an externalLinkRow
              for (let depth = $from.depth; depth >= 0; depth--) {
                const node = $from.node(depth);
                if (node.type.name === 'externalLinkRow') {
                  const resourceId = node.attrs.resourceId;
                  if (resourceId && paragraphRowRole($from.parent) !== 'subtitle') {
                    const newTitle = resourceRowTitleText(node);
                    if (newTitle.length > 0) {
                      debouncedResourceUpdate(resourceId, newTitle);
                    } else {
                      debouncedResourceUpdate(resourceId, '');
                    }
                  }
                  break;
                }
              }
              return false;
            },
          },
        },
      }),
    ];
  },

  addCommands() {
    return {}
  },

})
